import {
  getConsentUpdatedProperties,
  resolveConsentMethodFromBanner,
  resolveCookieYesConsentFromEvent,
  type CookieYesConsentUpdateDetail,
} from '@/lib/cookieyes';
import { captureConsentUpdated } from '@/lib/posthog';

type BannerAction = 'accept_all' | 'reject_all' | 'custom_preferences';

const actionFromElement = (element: HTMLElement): BannerAction | null => {
  const tag = (element.getAttribute('data-cky-tag') || '').trim().toLowerCase();
  const className = element.className?.toString().toLowerCase() || '';

  if (
    tag === 'detail-save-button' ||
    tag === 'save-button' ||
    tag === 'confirm-button' ||
    className.includes('cky-btn-accept-selected')
  ) {
    return 'custom_preferences';
  }

  if (tag === 'reject-button' || className.includes('cky-btn-reject')) {
    return 'reject_all';
  }

  if (tag === 'accept-button' || className.includes('cky-btn-accept')) {
    return 'accept_all';
  }

  return null;
};

const resolveBannerAction = (event: Event): BannerAction | null => {
  const path =
    typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    const action = actionFromElement(node);
    if (action) return action;
  }

  return null;
};

type StartConsentUpdatedTrackingOptions = {
  hadPreviousDecision: () => boolean;
  onTracked?: (event?: Event) => void;
};

export const startConsentUpdatedTracking = ({
  hadPreviousDecision,
  onTracked,
}: StartConsentUpdatedTrackingOptions): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  let pendingBannerAction: BannerAction | null = null;
  let lastPayloadKey = '';
  const startedAt = Date.now();

  const handleClick = (event: Event) => {
    const action = resolveBannerAction(event);
    if (action) {
      pendingBannerAction = action;
    }
  };

  const handleConsentUpdate = (event: Event) => {
    const bannerAction = pendingBannerAction;
    pendingBannerAction = null;

    const isHydrate =
      hadPreviousDecision() && !bannerAction && Date.now() - startedAt < 1500;

    if (isHydrate) return;

    window.setTimeout(() => {
      const consent = resolveCookieYesConsentFromEvent(event);
      if (!consent) return;

      const detail = (event as CustomEvent<CookieYesConsentUpdateDetail>).detail;
      const previousDecision = hadPreviousDecision();
      const consentMethod = resolveConsentMethodFromBanner({
        consent,
        hadPreviousDecision: previousDecision,
        detail,
        bannerAction,
      });

      const payloadKey = JSON.stringify(
        getConsentUpdatedProperties(consent, consentMethod)
      );
      if (payloadKey === lastPayloadKey) {
        onTracked?.(event);
        return;
      }

      captureConsentUpdated(consent, consentMethod, previousDecision);
      lastPayloadKey = payloadKey;
      onTracked?.(event);
    }, 50);
  };

  document.addEventListener('click', handleClick, true);
  document.addEventListener('cookieyes_consent_update', handleConsentUpdate);

  return () => {
    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('cookieyes_consent_update', handleConsentUpdate);
  };
};
